// The Tasks page's ONE status vocabulary and its rules, pure (no React) so
// taskModel.selftest.ts runs them on their own. A task's status is derived
// by the backend (services/tasks/views.task_status, build-spec §1.4); this
// file says it in words, says which group a task is in (Needs you ·
// Running now · Ended; the list's layout is listModel.ts, its filters
// filterModel.ts), says where an answer's delivery stands,
// checks the give form, says where the "tasks only" switch stands and
// turns a refusal into a sentence.
//
// Words (decisions): "task", "subagent", "schedule"; never "job",
// "background task" or "handoff" in this area's text.

import { isLiveTaskStatus, type GiveTaskBody, type TaskAskerOut, type TaskKind, type TaskStatus, type TaskSummary, type TaskSwitchOut, type TaskWorkerOut } from "@/api/tasks";
import { workflowError, workflowErrorCode } from "@/api/workflows";
import type { StatusTone } from "@/components/ui/StatusBadge";
import { ApiError } from "@/lib/api";
import { humanizeSlug } from "@/lib/format";

import { TEAM_MISSING_TEXT } from "../workflows/jobs/teamModel";
import { makeStatusWord, type StatusWord } from "../workflows/status";
import { minutesWords } from "../workflows/time";

const word = makeStatusWord;

/** Every task status, by its backend name: the word, tone, icon and look (features/workflows/status.ts). */
export const TASK_STATUS: Readonly<Record<TaskStatus, StatusWord>> = {
  awaiting_approval: word("Waiting for you", "warning", "approval"),
  queued: word("Queued", "neutral", "waiting", "waiting"),
  working: word("Working", "live", "running", "live"),
  waiting: word("Waiting for an answer", "info", "waiting", "live"),
  paused: word("Paused", "warning", "paused"),
  finished: word("Done", "success", "done"),
  partial: word("Partly done", "warning", "warning"),
  failed: word("Failed", "danger", "failed"),
  timed_out: word("Out of time", "warning", "timedOut"),
  stopped: word("Stopped", "neutral", "cancelled", "notRun"),
};

export const TASK_STATUSES = Object.keys(TASK_STATUS) as TaskStatus[];

const UNKNOWN = word("Unknown", "neutral", "unknown");

export function isTaskStatus(s: string | null | undefined): s is TaskStatus {
  return !!s && Object.prototype.hasOwnProperty.call(TASK_STATUS, s);
}

/** A task status's word; "Unknown" for one this dashboard does not know. */
export function taskStatusWord(status: string | null | undefined): StatusWord {
  return isTaskStatus(status) ? TASK_STATUS[status] : UNKNOWN;
}

/** Not ended yet (waiting for an OK, queued, working, waiting for an answer, paused). */
export function isLiveTask(t: { status: string }): boolean {
  return isLiveTaskStatus(t.status);
}

/** The operator is the one to act: an OK it waits for, or an answer that did not reach its asker. */
export function needsYou(t: Pick<TaskSummary, "status" | "answer_waiting">): boolean {
  return t.status === "awaiting_approval" || t.answer_waiting;
}

export const LIVE_STATUSES: readonly TaskStatus[] = ["queued", "working", "waiting", "paused"];

/**
 * A Team task whose plan waits for the OK: the task itself asked for none, and
 * its OK is the plan's, on the job's own page (jobsWaiting counts and lists it).
 */
export function planWaits(t: Pick<TaskSummary, "status" | "worker" | "approval">): boolean {
  return t.status === "awaiting_approval" && t.worker.kind === "team" && t.approval !== "required";
}

/**
 * What waits and what works right now, from the live list (LIVE_TASKS_QUERY)
 * and its counts: Home's tile and Working now, the sidebar and More badges,
 * Today's Needs you (UX review H1, H2). `needsYou` is the server's count
 * over every task (an OK it waits for, or an answer that did not reach its
 * asker, which can be on an ended task); `awaiting` the tasks waiting for
 * the OK, oldest first; `working` the rest of the live ones, oldest first.
 * A Team task whose plan waits is in neither count nor list: it is the Team
 * task's (jobsWaiting), so nothing is counted or listed twice.
 */
export function tasksNow(data: { tasks: readonly TaskSummary[]; counts: Readonly<Record<string, number>> } | undefined): {
  needsYou: number;
  awaiting: TaskSummary[];
  working: TaskSummary[];
} {
  const live = [...(data?.tasks ?? [])].filter((t) => isLiveTask(t)).sort((a, b) => a.created_at.localeCompare(b.created_at) || a.task_id.localeCompare(b.task_id));
  const plans = live.filter(planWaits).length;
  const awaiting = live.filter((t) => t.status === "awaiting_approval" && !planWaits(t));
  return {
    needsYou: Math.max((data?.counts?.needs_you ?? 0) - plans, awaiting.length),
    awaiting,
    working: live.filter((t) => t.status !== "awaiting_approval"),
  };
}

/**
 * Home's tile for the tasks that need you: "Task waiting for your OK" with
 * the first one named ("Momentum backtest with costs · Julia asked for
 * 2 h") and Review opening it; several, the Tasks page's Needs you; only
 * answers that did not arrive, "Answer not delivered".
 */
export function taskTile(
  needsYou: number,
  awaiting: readonly Pick<TaskSummary, "task_id" | "title" | "asker" | "minutes">[],
  nameOf: (template: string) => string,
  base = "/tasks",
): { title: string; detail: string; action: string; to: string } {
  const first = awaiting[0];
  const allOks = awaiting.length > 0 && awaiting.length >= needsYou;
  const title = awaiting.length === 0
    ? needsYou === 1 ? "Answer not delivered" : "Answers not delivered"
    : allOks ? `${needsYou === 1 ? "Task" : "Tasks"} waiting for you` : "Tasks need you";
  const detail = first
    ? `${first.title} · ${approvalLine(first, nameOf)}${awaiting.length > 1 ? ` · ${awaiting.length - 1} more` : ""}`
    : "Open the task to send it again";
  if (needsYou === 1 && first) return { title, detail, action: "Review", to: `${base}/${encodeURIComponent(first.task_id)}` };
  return { title, detail, action: needsYou === 1 ? "Open task" : "Review tasks", to: `${base}?status=needs_you` };
}

/** "Julia asked for 2 h" for a task waiting for the OK: who wants it, for how long. */
export function approvalLine(t: Pick<TaskSummary, "asker" | "minutes">, nameOf: (template: string) => string): string {
  const who = askerName(t.asker, nameOf);
  return `${who === "You" ? "You" : who} asked for ${minutesWords(t.minutes)}`;
}
export const ENDED_STATUSES: readonly TaskStatus[] = ["finished", "partial", "failed", "timed_out", "stopped"];

// ── the list's groups ──

export type TaskGroupKey = "needs_you" | "live" | "ended";

export const GROUP_LABEL: Readonly<Record<TaskGroupKey, string>> = { needs_you: "Needs you", live: "Running now", ended: "Ended" };

export function taskGroup(t: Pick<TaskSummary, "status" | "answer_waiting">): TaskGroupKey {
  if (needsYou(t)) return "needs_you";
  return isLiveTask(t) ? "live" : "ended";
}

/**
 * A scheduled run that ended with nothing to say (a check-in that found
 * nothing worth a line: it finished `NO_REPLY`, so nothing was sent;
 * docs/tasks/schedules-unified.md §6). Under its schedule's row in the
 * list such a run is quiet; a quiet schedule's real answers are not.
 */
export function isNothingToSay(t: Pick<TaskSummary, "asker" | "status" | "delivery_state" | "conclusion">): boolean {
  if (t.asker.kind !== "schedule" || t.status !== "finished" || t.delivery_state !== "not_needed") return false;
  const c = (t.conclusion ?? "").trim();
  return c === "" || /^NO_REPLY\b/i.test(c);
}

// ── who ──

/** The worker by name: an agent's or subagent's display name, "Team", or the workflow's. */
export function workerName(w: TaskWorkerOut, nameOf: (template: string) => string): string {
  if (w.kind === "team") return w.name || "Team";
  if (w.kind === "workflow") return w.name || humanizeSlug(w.workflow ?? "") || "A workflow";
  return w.name || (w.template ? nameOf(w.template) : "") || "An agent";
}

// Said mid-sentence ("asked by a schedule", "Answer sent to a to-do"): lower case.
const ASKER_KIND: Readonly<Record<string, string>> = { operator: "You", schedule: "a schedule", todo: "a to-do", task: "another task" };

/** Who asked, by name: "You" for the operator; an agent's name; else what started it ("a schedule"). */
export function askerName(a: TaskAskerOut, nameOf: (template: string) => string): string {
  if (a.kind === "operator") return "You";
  return a.name || (a.agent ? nameOf(a.agent) : "") || ASKER_KIND[a.kind] || "Someone";
}

const WHERE: Readonly<Record<string, string>> = { dm: "in a direct chat", dashboard: "in the dashboard chat", group: "in a group chat", task: "from inside a task" };

/** Where an agent asked, when it is worth saying ("in a group chat"); "" otherwise. */
export function whereWords(where: string | null | undefined): string {
  return (where && WHERE[where]) || "";
}

export function kindWord(kind: string): string {
  return kind === "question" ? "Question" : "Task";
}

/**
 * How a task's worker was chosen (`give --to auto`) and what the reply that
 * gave it offered (TaskDetail.routing), in words; null = not shown.
 */
export function routingWords(
  routing: { picked_by?: string | null; reason?: string | null; workflow_offer?: string | null } | null | undefined,
  worker: string,
): { picked: string | null; offer: string | null } {
  if (!routing) return { picked: null, offer: null };
  const reason = (routing.reason ?? "").trim().replace(/[.\s]+$/, "");
  const picked = routing.picked_by === "auto" ? `Corvus chose ${worker}${reason ? `: ${reason}` : ""}.` : null;
  const offer = routing.workflow_offer ? `The reply also offered: “${routing.workflow_offer}”` : null;
  return { picked, offer };
}

/** Who sent a follow-up or stopped a task: "you", an agent's name, "another task", "Lattice". */
export function byWords(by: string | null | undefined, nameOf: (template: string) => string): string {
  if (!by || by === "operator") return "you";
  if (by === "lattice") return "Corvus";
  if (by.startsWith("task:")) return "another task";
  return nameOf(by);
}

/** A word and a tone for a follow-up's kind and state, a chat question's state, and a note's kind. */
export const FOLLOW_UP_KIND: Readonly<Record<string, string>> = { follow_up: "Follow-up", answer: "An answer", no_answer: "No answer came", question: "A question" };

export const FOLLOW_UP_STATE: Readonly<Record<string, { label: string; tone: StatusTone }>> = {
  queued: { label: "Queued", tone: "neutral" },
  sent: { label: "Sent", tone: "success" },
  refused: { label: "Refused", tone: "danger" },
  cancelled: { label: "Stopped", tone: "neutral" },
};

export const QUESTION_STATE: Readonly<Record<string, { label: string; tone: StatusTone }>> = {
  open: { label: "Waiting for an answer", tone: "info" },
  answered: { label: "Answered", tone: "success" },
  expired: { label: "No answer in time", tone: "warning" },
  cancelled: { label: "Stopped", tone: "neutral" },
};

export const NOTE_KIND: Readonly<Record<string, string>> = { answer: "The answer", stopped: "That it was stopped", plan: "The plan", decision: "A decision", question: "A question", refused: "That it was refused" };

// ── the answer's delivery ──

export interface DeliveryWord {
  label: string;
  tone: StatusTone;
}

/**
 * Where the answer to its asker stands; null when no answer is owed
 * (not_needed, or none yet). Pending and uncertain are an answer waiting
 * (an uncertain one is never sent twice by itself: Resend), failed says so
 * (the task page puts Resend beside it).
 */
export function deliveryWords(state: string | null | undefined): DeliveryWord | null {
  switch (state) {
    case "pending":
    case "uncertain":
      return { label: "Answer waiting", tone: "warning" };
    case "sending":
      return { label: "Delivering", tone: "live" };
    case "delivered":
      return { label: "Delivered", tone: "success" };
    case "failed":
      return { label: "Not delivered", tone: "danger" };
    default:
      return null;
  }
}

// ── the give form ──

/** What the chosen worker is, for the form's rules ("agent" covers the operator's own agents). */
export type GiveWorkerKind = "agent" | "subagent" | "team" | "workflow";

export const QUESTION_MINUTES = 10;
export const TASK_DEFAULT_MINUTES = 60;
export const TASK_MAX_MINUTES = 240;
export const TEAM_MAX_MINUTES = 1440;
export const MIN_MINUTES = 5;
export const TEAM_MIN_MINUTES = 10;
export const MAX_TITLE = 120;
export const MAX_BRIEF = 6000;

export interface GiveForm {
  /** A template, "team" or "workflow:<slug>". */
  to: string;
  title: string;
  brief: string;
  kind: TaskKind;
  minutes: number;
  needsBrowser: boolean;
}

export const EMPTY_GIVE: GiveForm = { to: "", title: "", brief: "", kind: "task", minutes: TASK_DEFAULT_MINUTES, needsBrowser: false };

/** The worker's kind from the `to` value and the subagents' templates. */
export function giveWorkerKind(to: string, isSubagent: (template: string) => boolean): GiveWorkerKind {
  if (to === "team") return "team";
  if (to.startsWith("workflow:")) return "workflow";
  return isSubagent(to) ? "subagent" : "agent";
}

/** A question goes to an agent or a subagent only; the browser too. */
export const canAsk = (w: GiveWorkerKind) => w === "agent" || w === "subagent";

/** The time-limit rule: hidden for a workflow (its own limits), fixed for a question, else a range (the Team's from 10 minutes). */
export function minutesRule(kind: TaskKind, worker: GiveWorkerKind): { shown: boolean; fixed: number | null; min: number; max: number } {
  if (worker === "workflow") return { shown: false, fixed: null, min: MIN_MINUTES, max: TASK_MAX_MINUTES };
  if (kind === "question" && canAsk(worker)) return { shown: true, fixed: QUESTION_MINUTES, min: QUESTION_MINUTES, max: QUESTION_MINUTES };
  if (worker === "team") return { shown: true, fixed: null, min: TEAM_MIN_MINUTES, max: TEAM_MAX_MINUTES };
  return { shown: true, fixed: null, min: MIN_MINUTES, max: TASK_MAX_MINUTES };
}

export type GiveField = "to" | "title" | "brief" | "minutes";

/** What stops the form from being sent, by field ({} = it can go). */
export function giveProblems(form: GiveForm, worker: GiveWorkerKind): Partial<Record<GiveField, string>> {
  const out: Partial<Record<GiveField, string>> = {};
  if (!form.to) out.to = "Choose who does it.";
  if (!form.brief.trim()) out.brief = "Say what to do.";
  else if (form.brief.length > MAX_BRIEF) out.brief = `At most ${MAX_BRIEF.toLocaleString("en-GB")} characters (${form.brief.length.toLocaleString("en-GB")} now).`;
  if (form.title.length > MAX_TITLE) out.title = `At most ${MAX_TITLE} characters.`;
  const rule = minutesRule(form.kind, worker);
  if (rule.shown && rule.fixed === null && (!Number.isInteger(form.minutes) || form.minutes < rule.min || form.minutes > rule.max)) {
    out.minutes = `Between ${rule.min} and ${rule.max} minutes.`;
  }
  return out;
}

/** The request body: a question sends no minutes (its limit is fixed), a workflow neither. */
export function giveBody(form: GiveForm, worker: GiveWorkerKind): GiveTaskBody {
  const kind: TaskKind = canAsk(worker) ? form.kind : "task";
  const rule = minutesRule(kind, worker);
  const body: GiveTaskBody = {
    to: form.to,
    brief: form.brief.trim(),
    title: form.title.trim(),
    kind,
    needs_browser: canAsk(worker) && form.needsBrowser,
    params: {},
  };
  if (rule.shown && rule.fixed === null) body.minutes = form.minutes;
  return body;
}

// ── the "tasks only" switch ──

export interface SwitchNote {
  text: string;
  tone: "muted" | "success" | "warning" | "danger";
}

/**
 * Where the last apply of "Agents use tasks only" stands, as lines under
 * the switch. `expecting`: a save has just started one the read does not
 * show yet. Nothing for a switch never applied.
 */
export function switchNotes(sw: TaskSwitchOut | undefined, expecting = false): SwitchNote[] {
  const notes: SwitchNote[] = [];
  const state = sw?.state ?? "idle";
  if (expecting || state === "pending" || state === "running") notes.push({ text: "Applying…", tone: "muted" });
  else if (state === "done") notes.push(sw?.applied === "applied" ? { text: "Applied (hot reload confirmed)", tone: "success" } : { text: "Applied; the gateway has not reloaded yet", tone: "warning" });
  else if (state === "failed") notes.push({ text: `Failed: ${sw?.detail || "the apply did not finish"}`, tone: "danger" });
  if (sw?.skill_present === false) notes.push({ text: "The lattice-tasks skill is not installed in this workspace yet", tone: "warning" });
  return notes;
}

/** Whether a read shows the apply a save started at `sinceMs` (or a later one) has been picked up. */
export function switchCaughtUp(sw: TaskSwitchOut | undefined, sinceMs: number): boolean {
  if (!sw) return false;
  if (sw.state === "pending" || sw.state === "running") return true;
  const at = Date.parse(sw.requested_at ?? "");
  // A few seconds of slack: the server's clock and this one differ.
  return Number.isFinite(at) && at >= sinceMs - 5_000;
}

// ── refusals ──

const REFUSAL: Readonly<Record<string, string>> = {
  tasks_not_enabled: "Tasks are switched off for this workspace.",
  runtime_unsupported: "Tasks need OpenClaw 2026.9 on this workspace's gateway.",
  unknown_worker: "There is no agent or subagent by that name here.",
  family_worker: "A family agent cannot be given tasks.",
  draft_subagent: "That subagent is still a draft: publish it first.",
  browser_not_lent: "That subagent borrows another agent's browser, and only that agent can give it work.",
  jobs_not_enabled: "The team is not offered in this workspace.",
  team_missing: TEAM_MISSING_TEXT,
  workflows_not_enabled: "Workflows are switched off for this workspace.",
  unknown_workflow: "That workflow is not saved here any more.",
  bad_params: "Some of the workflow's settings are missing or not valid.",
  bad_minutes: "That time limit is not allowed for this task.",
  session_gone: "Its conversation was cleaned up after 14 days, so it cannot be followed up. Give a new task instead.",
  too_many_follow_ups: "This task has had as many follow-ups as it can take.",
  no_follow_up_for_kind: "This kind of task cannot be followed up.",
  awaiting_approval: "Give it your OK first.",
  not_live: "It has already ended.",
  not_awaiting_approval: "It is not waiting for your OK any more.",
  nothing_to_resend: "There is nothing to send again: the answer was delivered or is on its way.",
  needs_tasks_enabled: "Switch Tasks on first.",
};

/** A refusal in words: ours for a known code, else the server's own message. */
export function taskErrorText(error: unknown): string {
  const code = workflowErrorCode(error);
  const max = code === "bad_minutes" ? refusalNumber(error, "max_minutes") : null;
  if (max) return `That is longer than it may run: at most ${max} minutes.`;
  if (code && REFUSAL[code]) return REFUSAL[code];
  // nothing said why: never the request's path and status
  if (!code && error instanceof ApiError && error.status >= 500 && !(error.body as { detail?: unknown } | null)?.detail) return "That did not go through. Try again.";
  return workflowError(error);
}

/** A number the refusal carries beside its code (`max_minutes`), else null. */
function refusalNumber(error: unknown, key: string): number | null {
  if (!(error instanceof ApiError)) return null;
  const detail = (error.body as { detail?: unknown } | null)?.detail;
  const v = detail && typeof detail === "object" && !Array.isArray(detail) ? (detail as Record<string, unknown>)[key] : null;
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null;
}
